import React, { useEffect, useState } from "react";
import { Compass, CheckCircle2, Circle, ArrowRight, Trophy, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { BRANCH_META } from "@/lib/branches";
import { useAuth } from "@/context/AuthContext";

/**
 * Career Path Guide card — shown on the discipline page.
 * Presents an exemplar organization in this discipline with a 5-step
 * roadmap the learner can complete on Atlas. Progress is live: each step
 * ticks off as the user meets its metric target. Full completion drops a
 * badge on their resume.
 *
 * Not framed as active hiring — this is a career trajectory.
 */
export default function CareerPathCard({ disciplineSlug }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!disciplineSlug) return;
        setError(null);
        api.get(`/disciplines/${disciplineSlug}/career-path`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || null));
    }, [disciplineSlug]);

    const start = async () => {
        if (!data?.path?.slug || busy) return;
        setBusy(true);
        try {
            await api.post(`/career-paths/${data.path.slug}/start`);
            const { data: fresh } = await api.get(`/disciplines/${disciplineSlug}/career-path`);
            setData(fresh);
        } catch {
            // silent — button state doesn't matter for path definition
        } finally {
            setBusy(false);
        }
    };

    const dismiss = async () => {
        if (!data?.path?.slug || busy) return;
        setBusy(true);
        try {
            await api.post(`/career-paths/${data.path.slug}/dismiss`);
            const { data: fresh } = await api.get(`/disciplines/${disciplineSlug}/career-path`);
            setData(fresh);
        } catch {
            // silent
        } finally {
            setBusy(false);
        }
    };

    if (error) return null;
    if (!data) {
        return (
            <div className="crystal rounded-3xl p-6" data-testid="career-path-loading">
                <p className="text-sm text-slate-500">Loading your career path…</p>
            </div>
        );
    }

    const { path, progress, started, alternates_exhausted: exhausted } = data;
    const branchMeta = BRANCH_META[path.branch] || BRANCH_META.arts;
    const isStarted = !!started;
    const isCompleted = !!(started && started.completed_at);
    const pct = progress?.pct ?? 0;

    return (
        <div
            className="rounded-3xl p-6 sm:p-7 relative overflow-hidden"
            style={{
                background: `linear-gradient(135deg, ${branchMeta.soft} 0%, #FDFBF7 100%)`,
                borderTop: `4px solid ${branchMeta.color}`,
            }}
            data-testid="career-path-card"
        >
            {/* Decorative logo mark */}
            <div className="absolute top-6 right-6 opacity-10 pointer-events-none">
                <Compass className="w-24 h-24" color={branchMeta.color} />
            </div>

            <div className="relative">
                {/* Header — org identity */}
                <div className="flex items-start gap-3">
                    <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 font-display font-black text-white text-lg"
                        style={{
                            background: branchMeta.color,
                            boxShadow: `0 4px 20px ${branchMeta.color}44`,
                        }}
                        data-testid="career-path-org-mark"
                    >
                        {(path.org_name || "?")
                            .split(" ")
                            .slice(0, 2)
                            .map((w) => w[0])
                            .join("")}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: branchMeta.text }}>
                            Career path · {branchMeta.name}
                        </div>
                        <div
                            className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-slate-900 leading-tight"
                            data-testid="career-path-org-name"
                        >
                            {path.org_name}
                        </div>
                        <div className="text-sm text-slate-700 mt-0.5" data-testid="career-path-role">
                            Track: <span className="font-bold">{path.role_title}</span>
                        </div>
                    </div>
                    {isCompleted && (
                        <div
                            className="chip flex-shrink-0"
                            style={{ background: branchMeta.color, color: "#FFF" }}
                            data-testid="career-path-completed-chip"
                        >
                            <Trophy className="w-3 h-3" /> Completed
                        </div>
                    )}
                </div>

                {/* Mission + intro */}
                {path.mission && (
                    <p className="text-sm text-slate-700 mt-4 leading-relaxed" data-testid="career-path-mission">
                        {path.mission}
                    </p>
                )}
                {path.intro && (
                    <p className="text-sm text-slate-800 mt-3 italic border-l-2 pl-3" style={{ borderColor: branchMeta.color }} data-testid="career-path-intro">
                        {path.intro}
                    </p>
                )}

                {/* Progress bar (only if user + started or in progress) */}
                {user && progress && (
                    <div className="mt-5" data-testid="career-path-progress">
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-display font-bold text-slate-700">
                                {progress.done_steps}/{progress.total_steps} steps
                            </span>
                            <span className="font-mono text-slate-500">{pct}%</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-white/80 overflow-hidden">
                            <div
                                className="h-full transition-all duration-500 ease-out"
                                style={{ width: `${pct}%`, background: branchMeta.color }}
                            />
                        </div>
                    </div>
                )}

                {/* Roadmap steps */}
                <ol className="mt-5 space-y-2" data-testid="career-path-roadmap">
                    {(progress?.steps || path.roadmap || []).map((step, i) => {
                        const done = !!step.done;
                        return (
                            <li
                                key={i}
                                className="flex items-start gap-3 text-sm"
                                data-testid={`career-path-step-${i}`}
                            >
                                {done ? (
                                    <CheckCircle2
                                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                                        color={branchMeta.color}
                                        data-testid={`career-path-step-${i}-done`}
                                    />
                                ) : (
                                    <Circle
                                        className="w-5 h-5 flex-shrink-0 mt-0.5 text-slate-300"
                                        data-testid={`career-path-step-${i}-pending`}
                                    />
                                )}
                                <div className="min-w-0">
                                    <div
                                        className={
                                            done
                                                ? "text-slate-900 font-display font-bold line-through decoration-2 opacity-70"
                                                : "text-slate-900 font-display font-bold"
                                        }
                                    >
                                        {step.title}
                                    </div>
                                    {step.current !== undefined && (
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {step.current} / {step.target}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>

                {/* CTA */}
                <div className="mt-5 flex flex-wrap gap-2 items-center">
                    {!user && (
                        <a href="/" className="arcade-btn" data-variant="primary" data-testid="career-path-signup">
                            Sign up to track this path <ArrowRight className="w-4 h-4" />
                        </a>
                    )}
                    {user && !isStarted && !isCompleted && (
                        <button
                            onClick={start}
                            disabled={busy}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="career-path-start"
                        >
                            {busy ? "Starting…" : "Start this path"} <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                    {user && isStarted && !isCompleted && (
                        <span className="chip bg-slate-900/5 text-slate-700" data-testid="career-path-in-progress">
                            In progress
                        </span>
                    )}
                    {isCompleted && (
                        <span className="text-xs text-slate-600">
                            This path is stamped on your resume.
                        </span>
                    )}
                    {user && !isCompleted && !exhausted && (
                        <button
                            onClick={dismiss}
                            disabled={busy}
                            className="text-xs text-slate-500 hover:text-slate-900 underline font-display font-bold ml-auto disabled:opacity-40"
                            data-testid="career-path-dismiss"
                            title="Get a different exemplar org for this discipline"
                        >
                            <RefreshCw className="w-3 h-3 inline mr-1" />
                            Not for me — show me another
                        </button>
                    )}
                    {user && !isCompleted && exhausted && (
                        <span
                            className="text-xs text-slate-500 italic ml-auto"
                            data-testid="career-path-exhausted"
                            title="You've seen every exemplar we have for this discipline"
                        >
                            You've explored every exemplar we have here.
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
